import { throttle } from 'es-toolkit';

interface ExitIntentProps {
    cursorEventThrottle?: number;
    maxDisplays?: number;
    onExiting?: (flag: boolean) => void;
    scrollBackPercent?: number;
    scrollEventThrottle?: number;
    scrollMinimumPercent?: number;
    threshold?: number;
}

export default function ExitIntent(options: ExitIntentProps) {
    const {
        cursorEventThrottle = 200,
        maxDisplays = 1,
        onExiting,
        scrollBackPercent = 10,
        scrollEventThrottle = 500,
        scrollMinimumPercent = 50,
        threshold = 20,
    } = options;

    const config: ExitIntentProps = {
        cursorEventThrottle,
        maxDisplays,
        onExiting,
        scrollBackPercent,
        scrollEventThrottle,
        scrollMinimumPercent,
        threshold,
    };

    return setupExitIntent(config);
}

function setupExitIntent(config: ExitIntentProps) {
    const eventListeners = new Map();
    let displays = 0;
    let lowestScrollPosition = 0;
    const screenHeight = window.screen.height;

    const addEvent = (eventName: string, callback: any) => {
        document.addEventListener(eventName, callback, false);
        eventListeners.set(`document:${eventName}`, { callback, eventName });
    };

    const removeEvent = (key: string) => {
        const { callback, eventName } = eventListeners.get(key);
        document.removeEventListener(eventName, callback);
        eventListeners.delete(key);
    };

    const shouldDisplayFromCursorEvent = (position: number, threshold: number, maxDisplays: number) => {
        if (position <= threshold && displays < maxDisplays) {
            displays++;
            return true;
        }
        return false;
    };

    const shouldDisplayFromScrollEvent = (
        currPosition: number,
        totalHeight: number,
        scrollMinimumPercent: number,
        scrollBackPercent: number
    ) => {
        // if the user has scrolled up more than scrollBackPercent of the total scroll area height,
        // AFTER scrolling past scrollMinimumPercent of content, they're probably exiting
        const scrollLimit = totalHeight * (scrollMinimumPercent / 100);
        const scrollBackMax = totalHeight * (scrollBackPercent / 100);
        const scrollBackHeight = lowestScrollPosition - currPosition;
        if (lowestScrollPosition > scrollLimit && scrollBackHeight > scrollBackMax) {
            displays++;
            return true;
        }
        return false;
    };

    const mouseDidMove = (event: { clientX: number; clientY: number }) => {
        if (shouldDisplayFromCursorEvent(event.clientY, config?.threshold || 0, config?.maxDisplays || 0)) {
            config?.onExiting(true);
            if (displays >= config?.maxDisplays) {
                removeEvents();
            }
        }
    };

    const scrolled = (event: {
        srcElement: {
            documentElement: { scrollHeight: number; scrollTop: number };
        };
    }) => {
        const scrollHeight = event.srcElement.documentElement.scrollHeight - screenHeight;
        const currPosition = event.srcElement.documentElement.scrollTop;
        lowestScrollPosition = currPosition > lowestScrollPosition ? currPosition : lowestScrollPosition;
        if (
            shouldDisplayFromScrollEvent(
                currPosition,
                scrollHeight,
                config.scrollMinimumPercent || 0,
                config.scrollBackPercent || 0
            )
        ) {
            config?.onExiting(true);
            if (displays >= config?.maxDisplays || 0) {
                removeEvents();
            }
        }
    };

    const removeEvents = () => {
        eventListeners.forEach((value, key) => removeEvent(key));
    };

    addEvent('mousemove', throttle(mouseDidMove, config.cursorEventThrottle));
    addEvent('scroll', throttle(scrolled, config.scrollEventThrottle));

    return removeEvents;
}
